export function isAddModulesResult(result) {
    return result.mode === "add-modules";
}
const PLATFORM_ADMIN_ROLES = ["TENANT_ADMIN"];
function uniqueSorted(values) {
    return [...new Set(values)].sort();
}
function collectProfileRoles(def, moduleId, addModuleRole, platformRoles, permissions) {
    for (const role of def.iamRoles ?? [])
        addModuleRole(moduleId, role);
    for (const role of def.platformRoles ?? [])
        platformRoles.add(role);
    for (const perm of def.iamPermissions ?? [])
        permissions.add(perm);
}
export function resolvePackageProvisioning(selection, index) {
    const iamRolesByModule = new Map();
    const platformRoles = new Set(PLATFORM_ADMIN_ROLES);
    const permissions = new Set();
    const addModuleRole = (moduleId, role) => {
        if (!iamRolesByModule.has(moduleId))
            iamRolesByModule.set(moduleId, new Set());
        iamRolesByModule.get(moduleId).add(role);
    };
    for (const moduleId of selection.selectedModules) {
        const profileIds = selection.moduleProfiles[moduleId] ?? [];
        if (profileIds.length > 0) {
            for (const profileId of profileIds) {
                const def = index.profileCatalog[profileId];
                if (!def || def.moduleId !== moduleId)
                    continue;
                collectProfileRoles(def, moduleId, addModuleRole, platformRoles, permissions);
            }
            continue;
        }
        const defaults = index.moduleDefaultIamRoles?.[moduleId] ?? [];
        for (const role of defaults)
            addModuleRole(moduleId, role);
    }
    const result = {
        iamRolesByModule: {},
        platformRoles: uniqueSorted([...platformRoles]),
        iamPermissions: uniqueSorted([...permissions]),
    };
    for (const [moduleId, roles] of iamRolesByModule) {
        result.iamRolesByModule[moduleId] = uniqueSorted([...roles]);
    }
    return result;
}
export function formatProvisioningPlan(plan, index, locale = "fr") {
    const lines = [];
    const catalog = index.iamRoleCatalog ?? {};
    if (plan.platformRoles.length) {
        const labels = plan.platformRoles.map((slug) => {
            const entry = catalog[slug];
            if (!entry)
                return slug;
            return locale === "en" ? entry.nameEn ?? entry.nameFr : entry.nameFr;
        });
        lines.push(locale === "en"
            ? `Platform roles: ${labels.join(", ")}`
            : `Rôles plateforme : ${labels.join(", ")}`);
    }
    for (const [moduleId, roles] of Object.entries(plan.iamRolesByModule)) {
        const labels = roles.map((slug) => {
            const entry = catalog[slug];
            if (!entry)
                return slug;
            return locale === "en" ? entry.nameEn ?? entry.nameFr : entry.nameFr;
        });
        lines.push(locale === "en"
            ? `IAM ${moduleId}: ${labels.join(", ")}`
            : `IAM ${moduleId} : ${labels.join(", ")}`);
    }
    if (plan.iamPermissions.length) {
        lines.push(locale === "en"
            ? `Extra permissions: ${plan.iamPermissions.join(", ")}`
            : `Permissions additionnelles : ${plan.iamPermissions.join(", ")}`);
    }
    return lines;
}
export const MARKETPLACE_TYPE_LABELS = {
    module: "Module",
    extension: "Extension",
    connector: "Connecteur",
    template: "Template",
};
export const MARKETPLACE_STATUS_LABELS = {
    available: "Disponible",
    beta: "Bêta",
    coming_soon: "Bientôt",
};
export const PARTNER_TYPE_LABELS = {
    integrator: "Intégrateur & ESN",
    reseller: "Revendeur",
    ngo: "ONG & bailleur",
    hosting: "Hébergeur",
    technology: "Partenaire technologique",
};
export const PARTNER_TIER_LABELS = {
    bronze: "Bronze",
    silver: "Silver",
    gold: "Gold",
    platinum: "Platinum",
};
/** Profils établissement : récompense = réduction facture, pas mobile money. */
export const INSTITUTION_AFFILIATE_PROFILES = [
    "school",
    "hospital",
    "enterprise",
];
export function isInstitutionAffiliateProfile(profile) {
    return INSTITUTION_AFFILIATE_PROFILES.includes(profile);
}
export const AFFILIATE_PROFILE_LABELS = {
    individual: "Particulier",
    teacher: "Enseignant",
    student: "Étudiant",
    school: "École / établissement scolaire",
    hospital: "Hôpital / clinique",
    enterprise: "Entreprise",
    parent: "Parent d'élève",
    other: "Autre",
};
export const INDUSTRY_LABELS = {
    healthcare: "Santé",
    education: "Éducation",
    mining: "Mines & ressources",
    retail: "Commerce & distribution",
    logistics: "Logistique",
    security: "Sécurité privée",
    "professional-services": "Services professionnels",
    ngo: "ONG & bailleurs",
    "public-sector": "Secteur public",
    sme: "PME",
    enterprise: "Grand compte",
    services: "Services",
    esn: "ESN & conseil",
    manufacturing: "Industrie",
    pharmacy: "Pharmacie",
    subcontracting: "Sous-traitance",
    "security-services": "Services de sécurité",
    "cross-industry": "Tous secteurs",
    "real-estate": "Immobilier",
    events: "Événementiel",
};
export const STATUS_LABELS = {
    ga: "Disponible",
    beta: "Bêta",
    alpha: "Alpha",
    reserved: "Bientôt",
    planned: "En développement",
};
export const CATEGORY_LABELS = {
    platform: "Plateforme",
    operations: "Opérations",
    finance: "Finance",
    hr: "Ressources humaines",
    vertical: "Solution verticale",
    infrastructure: "Infrastructure",
    erp: "ERP",
    integration: "Intégration",
};
export { PEP_ALLOWED_STAGING_SEO_HOSTS, PEP_CANONICAL_PRODUCTION_HOST, PEP_CANONICAL_PRODUCTION_ORIGIN, PEP_FORBIDDEN_SEO_HOSTS, PepSeoValidationError, assertValidPepSeoBaseUrl, getPepOriginHost, isAllowedStagingSeoHost, isForbiddenSeoHost, normalizePepOrigin, resolvePepRuntimeSiteUrl, resolvePepSeoBaseUrl, resolvePepSeoEnv, } from "./seo-site-url.js";
export { ACTIVATION_INTENTS_ACTIVE_V1, BILLING_VARIABLE_CATALOGUE_EXAMPLES, DEPLOYMENT_MODES_ACTIVE_V1, PLATFORM_PUBLIC_CAPABILITIES, SLA_TIERS_ACTIVE_V1, SOLUTION_REQUEST_V1_WIRE, mapActivationModeToIntent, mapContactDeploymentToDeploymentMode, } from "./platform-architecture.js";
export { ITAAS_CURRENT_ADDRESS, ITAAS_HISTORICAL_ADDRESS_KINSHASA, ITAAS_INSTITUTION, ITAAS_INSTITUTIONAL_WEBSITE, ITAAS_LEGAL_IDENTIFIERS, ITAAS_OPERATIONAL_LEADERSHIP, ITAAS_ORGANIZATION_ID, ITAAS_PUBLIC_EMAIL, ITAAS_PUBLIC_PHONE, ITAAS_PUBLIC_PHONE_TEL, ITAAS_SUITE_WEBSITE, buildItaasOrganizationJsonLd, buildPepWebSiteJsonLd, publicLegalIdentifierLines, } from "./institutional.js";
